// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xProductPartNumbersStyles from "@guestsupply/App/SharedStyles/ProductPartNumbersStyles";
import { shippingWeightUnit } from "@guestsupply/XDL/constants";
import ShippingWeight from "@guestsupply/XDL/Icons/ShippingWeight";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { Icon } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // XNG-CHANGES
    customerPartNumber?: String;
    showShippingWeight?: boolean;
    showEligibleIcon?: boolean;
    shippingWeight?: string;
    packDescription?: string;
    trackingNumber?: string;
    status?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // XNG-Changes
    shippingWeightGridItem?: GridItemProps;
    casePackGridItem?: GridItemProps;
    shippingWeightLabelText?: TypographyPresentationProps;
    shippingWeightValueText?: TypographyPresentationProps;
    shippingWeightWrapper?: InjectableCss;
    icon?: IconProps;
    trackingNumberGridItem?: GridItemProps;
    trackingNumberLabelText?: TypographyPresentationProps;
    trackingNumberValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    customerPartNumber,
    showShippingWeight = true,
    shippingWeight,
    packDescription,
    showEligibleIcon,
    trackingNumber,
    status,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {customerPartNumber?.length && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && (
                        <Typography {...styles.erpNumberLabelText}>{translate("Customer Part #")}</Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Customer Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {customerPartNumber}
                    </Typography>
                </GridItem>
            )}
            {showProductNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
            {showShippingWeight && !!shippingWeight && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.shippingWeightGridItem}>
                    {showLabel ? (
                        <Typography {...styles.shippingWeightLabelText}>{translate("Shipping Weight")}</Typography>
                    ) : (
                        <VisuallyHidden>
                            <Typography {...styles.shippingWeightLabelText}>{translate("Shipping Weight")}</Typography>
                        </VisuallyHidden>
                    )}
                    <StyledWrapper {...styles.shippingWeightWrapper}>
                        {shippingWeight.toLowerCase() !== "n/a" ? (
                            <Typography {...styles.shippingWeightValueText}>
                                {`${parseFloat(shippingWeight.toString()).toString()}`}
                                {shippingWeightUnit}
                            </Typography>
                        ) : (
                            <Typography {...styles.shippingWeightValueText}>{shippingWeight}</Typography>
                        )}
                        {showEligibleIcon && <Icon {...styles.icon} />}
                    </StyledWrapper>
                </GridItem>
            )}
            {!!packDescription && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.casePackGridItem}>
                    {showLabel ? (
                        <Typography {...styles.shippingWeightLabelText}>{translate("Case Pack")}</Typography>
                    ) : (
                        <VisuallyHidden>
                            <Typography {...styles.shippingWeightLabelText}>{translate("Case Pack")}</Typography>
                        </VisuallyHidden>
                    )}

                    <Typography {...styles.shippingWeightValueText}>{`${packDescription} `}</Typography>
                </GridItem>
            )}
            {!!trackingNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.trackingNumberGridItem}>
                    {showLabel ? (
                        <Typography {...styles.trackingNumberLabelText}>{translate("Tracking #")}</Typography>
                    ) : (
                        <VisuallyHidden>
                            <Typography {...styles.trackingNumberLabelText}>{translate("Tracking #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.trackingNumberValueText} data-test-selector="trackingNumber">
                        {trackingNumber}
                    </Typography>
                </GridItem>
            )}
            {(status === "O" || status === "X") && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.casePackGridItem}>
                    <Typography {...xProductPartNumbersStyles.label}>{translate("Status")}</Typography>
                    <Typography {...xProductPartNumbersStyles.value} data-test-selector="orderLine-Status">
                        {status === "O" && translate("Backorder")}
                        {status === "X" && translate("Cancelled")}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;
